import { getNumericRef, calculateStatusProgress } from './GanttUtils';

/**
 * Builds hierarchical row data for Gantt chart rendering.
 * Mode-specific date logic is injected via dateHelpers.
 *
 * @param {Array} tasks - Main tasks
 * @param {Array} statuses - Status columns
 * @param {Array} epics - Epic groups
 * @param {Array} subtasks - All subtasks
 * @param {Object} expandedItems - Expanded/collapsed state by row ID
 * @param {Object} dateHelpers - Mode-specific date functions:
 *   - getEffectiveDates(item) => { start, end }
 *   - calculateSummaryDates(children) => { start, end, progress }
 *   - calculateDateRange(rowList) => object (mode-specific date range)
 *   - taskStatuses - Array of subtask status definitions (for progress calculation)
 * @returns {{ rows: Array, dateRange: Object }}
 */
export const buildGanttRows = (tasks, statuses, epics, subtasks, expandedItems, dateHelpers) => {
    const rowList = [];

    // Build a map of subtasks by mainTaskId (keyed by projectId:mainTaskId)
    const subtasksByMainTask = {};
    for (const st of subtasks) {
        const parentId = st.mainTaskId;
        if (parentId) {
            const key = `${st.projectId || 'default'}:${parentId}`;
            if (!subtasksByMainTask[key]) subtasksByMainTask[key] = [];
            subtasksByMainTask[key].push(st);
        }
    }

    // Helper to get sorted subtasks for a parent task (sort by order field)
    const getSortedSubtasks = (parentId, projectId) => {
        const key = `${projectId || 'default'}:${parentId}`;
        const subs = subtasksByMainTask[key] || [];
        return [...subs].sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    // Build a map of tasks by status ID
    const tasksByStatus = {};

    for (const task of tasks) {
        const statusId = task.statusId;
        if (statusId) {
            if (!tasksByStatus[statusId]) tasksByStatus[statusId] = [];
            tasksByStatus[statusId].push(task);
        }
    }

    // Helper to get task's effective dates (from subtasks if any, otherwise own dates)
    const getTaskEffectiveDates = (task) => {
        const taskSubtasks = getSortedSubtasks(task.taskId, task.projectId);
        if (taskSubtasks.length > 0) {
            const summary = dateHelpers.calculateSummaryDates(taskSubtasks.map(st => {
                const dates = dateHelpers.getEffectiveDates(st);
                return {
                    start: dates.start,
                    end: dates.end,
                    progress: calculateStatusProgress(st, dateHelpers.taskStatuses)
                };
            }));
            const taskDates = dateHelpers.getEffectiveDates(task);
            return {
                start: summary.start || taskDates.start,
                end: summary.end || taskDates.end,
                progress: summary.progress
            };
        }
        const taskDates = dateHelpers.getEffectiveDates(task);
        return {
            start: taskDates.start,
            end: taskDates.end,
            progress: task.progress || 0
        };
    };

    // Helper to get sorted statuses (by order field)
    const getSortedStatuses = () => {
        return [...statuses].sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    // Helper to get sorted tasks for a status (sort by order field)
    const getSortedTasksForStatus = (statusId) => {
        const statusTasks = tasksByStatus[statusId] || [];
        const tasksWithSubtasks = statusTasks.filter(t => getSortedSubtasks(t.taskId, t.projectId).length > 0);
        // Sort by swimlane order first, then by task order
        return [...tasksWithSubtasks].sort((a, b) => {
            const swimlaneDiff = (a.swimlaneOrder || 0) - (b.swimlaneOrder || 0);
            if (swimlaneDiff !== 0) return swimlaneDiff;
            return (a.order || 0) - (b.order || 0);
        });
    };

    // Track tasks already processed by status loop to avoid duplicates in epic loop
    const processedTaskIds = new Set();

    // Process statuses (sorted by order)
    for (const status of getSortedStatuses()) {
        const tasksWithSubtasks = getSortedTasksForStatus(status.id);

        // Skip statuses with no tasks that have subtasks
        if (tasksWithSubtasks.length === 0) continue;

        const isExpanded = expandedItems[`status_${status.id}`] !== false; // Default expanded

        // Calculate status dates from its tasks (using their effective dates which span subtasks)
        const statusSummary = dateHelpers.calculateSummaryDates(tasksWithSubtasks.map(t => getTaskEffectiveDates(t)));

        rowList.push({
            id: `status_${status.id}`,
            originalId: status.id,
            type: 'status',
            text: status.name,
            start: statusSummary.start,
            end: statusSummary.end,
            progress: statusSummary.progress,
            level: 0,
            hasChildren: tasksWithSubtasks.length > 0,
            isExpanded,
            isSummary: true,
            childCount: tasksWithSubtasks.length,
            orderKey: '_statuses',
            isClosed: status.isClosed
        });

        // Mark all tasks in this status as processed (even if collapsed)
        for (const task of tasksWithSubtasks) {
            processedTaskIds.add(`${task.projectId || 'default'}:${task.taskId}`);
        }

        // Add child tasks if expanded
        if (isExpanded) {
            for (const task of tasksWithSubtasks) {
                const taskSubtasks = getSortedSubtasks(task.taskId, task.projectId);
                const taskRowId = `task_${task.projectId || 'default'}_${task.taskId}`;
                const isTaskExpanded = expandedItems[taskRowId] !== false;

                // Calculate task dates - tasks with subtasks ALWAYS span their subtasks
                const taskDates = dateHelpers.getEffectiveDates(task);
                let taskStart = taskDates.start;
                let taskEnd = taskDates.end;
                let taskProgress = task.progress || 0;

                const taskSummary = dateHelpers.calculateSummaryDates(taskSubtasks.map(st => {
                    const stDates = dateHelpers.getEffectiveDates(st);
                    return {
                        start: stDates.start,
                        end: stDates.end,
                        progress: calculateStatusProgress(st, dateHelpers.taskStatuses)
                    };
                }));
                // Tasks ALWAYS span their subtasks (override task's own dates)
                if (taskSummary.start) taskStart = taskSummary.start;
                if (taskSummary.end) taskEnd = taskSummary.end;
                taskProgress = taskSummary.progress;

                rowList.push({
                    id: `task_${task.projectId || 'default'}_${task.taskId}`,
                    originalId: task.taskId,
                    projectId: task.projectId,
                    type: 'task',
                    text: task.subject || task.text,
                    start: taskStart,
                    end: taskEnd,
                    progress: taskProgress,
                    level: 1,
                    hasChildren: taskSubtasks.length > 0,
                    isExpanded: isTaskExpanded,
                    isSummary: taskSubtasks.length > 0,
                    status: status.name,
                    isClosed: task.isClosed,
                    childCount: taskSubtasks.length,
                    swimlaneId: task.swimlaneId,
                    orderKey: `_status_${status.id}`,
                    parentStatusId: status.id,
                    // T471/T475 - effort rides the row so the bar label and
                    // the left panel both read it. MAIN TASK pushes ONLY:
                    // subtasks carry no effort (D3). BOTH pushes need it -
                    // one is the status grouping, one the epic grouping, and
                    // a field added to one appears in that mode alone.
                    effortEstimated: task.effortEstimated,
                    effortActual: task.effortActual,
                    _isAddin: task._isAddin,
                    _addinColour: task._addinColour
                });

                // Add subtasks if expanded
                if (isTaskExpanded) {
                    for (const subtask of taskSubtasks) {
                        const subtaskRef = getNumericRef(subtask.taskId);
                        const stDates = dateHelpers.getEffectiveDates(subtask);
                        rowList.push({
                            id: `subtask_${subtask.projectId || 'default'}_${subtask.taskId}`,
                            originalId: subtask.taskId,
                            projectId: subtask.projectId,
                            ref: subtaskRef,
                            type: 'subtask',
                            text: subtask.subject || subtask.text,
                            start: stDates.start,
                            end: stDates.end,
                            progress: calculateStatusProgress(subtask, dateHelpers.taskStatuses),
                            level: 2,
                            hasChildren: false,
                            isExpanded: false,
                            isSummary: false,
                            status: subtask.statusName || subtask.status,
                            statusId: subtask.status,
                            isClosed: subtask.isClosed,
                            mainTaskId: task.taskId,
                            assignee: subtask.assignedToName || null,
                            assignedTo: subtask.assignedTo || null,
                            isMeeting: subtask.isMeeting || false,
                            meetingType: subtask.meetingType || null,
                            agenda: subtask.agenda || null,
                            links: subtask.links || [],
                            sourceTemplateId: subtask._sourceTemplateId
                        });
                    }
                }
            }
        }
    }

    // Process Epics (for edge cases where tasks are assigned to Epics)
    const getSortedEpics = () => {
        return [...epics].sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    // Helper to get sorted tasks for an epic (skip tasks already in status groups)
    const getSortedTasksForEpic = (epicId) => {
        const epicTasks = tasks.filter(t =>
            String(t.statusId) === String(epicId) &&
            getSortedSubtasks(t.taskId, t.projectId).length > 0 &&
            !processedTaskIds.has(`${t.projectId || 'default'}:${t.taskId}`)
        );
        // Sort by swimlane order first, then by task order
        return [...epicTasks].sort((a, b) => {
            const swimlaneDiff = (a.swimlaneOrder || 0) - (b.swimlaneOrder || 0);
            if (swimlaneDiff !== 0) return swimlaneDiff;
            return (a.order || 0) - (b.order || 0);
        });
    };

    for (const epic of getSortedEpics()) {
        const tasksWithSubtasks = getSortedTasksForEpic(epic.epicId);

        // Skip epics with no tasks that have subtasks
        if (tasksWithSubtasks.length === 0) continue;

        const isExpanded = expandedItems[`epic_${epic.epicId}`] !== false; // Default expanded

        // Calculate epic dates from its tasks
        const epicSummary = dateHelpers.calculateSummaryDates(tasksWithSubtasks.map(t => getTaskEffectiveDates(t)));

        rowList.push({
            id: `epic_${epic.epicId}`,
            originalId: epic.epicId,
            type: 'epic',
            text: epic.subject,
            start: epicSummary.start,
            end: epicSummary.end,
            progress: epicSummary.progress,
            level: 0,
            hasChildren: tasksWithSubtasks.length > 0,
            isExpanded,
            isSummary: true,
            childCount: tasksWithSubtasks.length,
            orderKey: '_epics'
        });

        // Add child tasks if expanded
        if (isExpanded) {
            for (const task of tasksWithSubtasks) {
                const taskSubtasks = getSortedSubtasks(task.taskId, task.projectId);
                const taskRowId = `task_${task.projectId || 'default'}_${task.taskId}`;
                const isTaskExpanded = expandedItems[taskRowId] !== false;

                // Calculate task dates - tasks with subtasks ALWAYS span their subtasks
                const taskDates = dateHelpers.getEffectiveDates(task);
                let taskStart = taskDates.start;
                let taskEnd = taskDates.end;
                let taskProgress = task.progress || 0;

                const taskSummary = dateHelpers.calculateSummaryDates(taskSubtasks.map(st => {
                    const stDates = dateHelpers.getEffectiveDates(st);
                    return {
                        start: stDates.start,
                        end: stDates.end,
                        progress: calculateStatusProgress(st, dateHelpers.taskStatuses)
                    };
                }));
                if (taskSummary.start) taskStart = taskSummary.start;
                if (taskSummary.end) taskEnd = taskSummary.end;
                taskProgress = taskSummary.progress;

                rowList.push({
                    id: `task_${task.projectId || 'default'}_${task.taskId}`,
                    originalId: task.taskId,
                    projectId: task.projectId,
                    type: 'task',
                    text: task.subject || task.text,
                    start: taskStart,
                    end: taskEnd,
                    progress: taskProgress,
                    level: 1,
                    hasChildren: taskSubtasks.length > 0,
                    isExpanded: isTaskExpanded,
                    isSummary: taskSubtasks.length > 0,
                    status: epic.subject,
                    isClosed: task.isClosed,
                    childCount: taskSubtasks.length,
                    swimlaneId: task.swimlaneId,
                    orderKey: `_epic_${epic.epicId}`,
                    parentEpicId: epic.epicId,
                    // T471/T475 - effort rides the row so the bar label and
                    // the left panel both read it. MAIN TASK pushes ONLY:
                    // subtasks carry no effort (D3). BOTH pushes need it -
                    // one is the status grouping, one the epic grouping, and
                    // a field added to one appears in that mode alone.
                    effortEstimated: task.effortEstimated,
                    effortActual: task.effortActual,
                    _isAddin: task._isAddin,
                    _addinColour: task._addinColour
                });

                // Add subtasks if expanded
                if (isTaskExpanded) {
                    for (const subtask of taskSubtasks) {
                        const subtaskRef = getNumericRef(subtask.taskId);
                        const stDates = dateHelpers.getEffectiveDates(subtask);
                        rowList.push({
                            id: `subtask_${subtask.projectId || 'default'}_${subtask.taskId}`,
                            originalId: subtask.taskId,
                            projectId: subtask.projectId,
                            ref: subtaskRef,
                            type: 'subtask',
                            text: subtask.subject || subtask.text,
                            start: stDates.start,
                            end: stDates.end,
                            progress: calculateStatusProgress(subtask, dateHelpers.taskStatuses),
                            level: 2,
                            hasChildren: false,
                            isExpanded: false,
                            isSummary: false,
                            status: subtask.statusName || subtask.status,
                            statusId: subtask.status,
                            isClosed: subtask.isClosed,
                            mainTaskId: task.taskId,
                            assignee: subtask.assignedToName || null,
                            assignedTo: subtask.assignedTo || null,
                            isMeeting: subtask.isMeeting || false,
                            meetingType: subtask.meetingType || null,
                            agenda: subtask.agenda || null,
                            links: subtask.links || [],
                            sourceTemplateId: subtask._sourceTemplateId
                        });
                    }
                }
            }
        }
    }

    // Calculate date range using mode-specific helper
    // Note: calculateDateRange may mutate rowList (e.g. unshifting project row)
    const dateRange = dateHelpers.calculateDateRange(rowList);

    return {
        rows: rowList,
        dateRange
    };
};
